/**
 * Every hydration mismatch Vue reports starts its warning with this word, and
 * nothing else Vue warns about in a production build does.
 */
const DETAILED_PREFIX = 'Hydration '

/** What Vue puts in front of a warning on its way to the console. */
const VUE_WARN_PREFIX = '[Vue warn]: '

/**
 * The one line Vue logs through `console.error` at the end of a hydration that
 * found anything, whether or not the build compiled the detailed warnings in.
 */
const SUMMARY = 'Hydration completed but contains mismatches'

/**
 * How much of the first warning travels with the report: its category only,
 * the kind of mismatch and the element, up to the first colon. The rendered
 * and expected values after it are whatever the page drew, which on a rendered
 * home page can be visitor specific, so they never leave the browser.
 */
const SAMPLE_LENGTH = 200

/** The category of a Vue hydration warning, without the values it compared. */
export function hydrationMismatchCategory(text: string): string {
  return text.split(/[:\n]/, 1)[0].trim().slice(0, SAMPLE_LENGTH)
}

export interface HydrationMismatchCounter {
  /** A line Vue wrote to console.warn. True when it named a mismatch. */
  recordWarning: (message: unknown) => boolean
  /** Vue's end of hydration error. True when it was the mismatch summary. */
  recordSummary: (message: unknown) => boolean
  /** Mismatches on this page view. */
  total: () => number
  /** The category of the first mismatch Vue named, for the report. */
  sample: () => string | undefined
}

/**
 * Counts the hydration mismatches of one page view.
 *
 * A build with `__VUE_PROD_HYDRATION_MISMATCH_DETAILS__` on warns once per
 * mismatch, which is the real count. A build without it only logs the summary
 * line, and then all the counter can honestly say is one.
 */
export function createHydrationMismatchCounter(): HydrationMismatchCounter {
  let detailed = 0
  let summary = false
  let first: string | undefined

  return {
    recordWarning(message: unknown): boolean {
      if (typeof message !== 'string') return false
      const text = message.startsWith(VUE_WARN_PREFIX)
        ? message.slice(VUE_WARN_PREFIX.length)
        : message
      if (!text.startsWith(DETAILED_PREFIX)) return false
      detailed += 1
      first ??= hydrationMismatchCategory(text)
      return true
    },

    recordSummary(message: unknown): boolean {
      if (typeof message !== 'string') return false
      if (!message.includes(SUMMARY)) return false
      summary = true
      return true
    },

    total(): number {
      if (detailed > 0) return detailed
      return summary ? 1 : 0
    },

    sample(): string | undefined {
      return first
    },
  }
}
